<template>
  <div id="app">
    <!-- Componente que maneja la selección de empresa, se pasa el evento "empresaSeleccionada" al método "obtenerEmpresa" -->
    <div id="nav-empresa-seleccionada" v-if="!ocultarNavegacion">
      <EmpresaSeleccionada @empresaSeleccionada="obtenerEmpresa" />
    </div>

    <!-- 
      El Navbar solo se renderiza si la ruta actual NO es "Entrada".
      Esto se maneja mediante la comprobación del nombre de la ruta usando "$route.name".
      Si la ruta actual es "Entrada", el Navbar no se mostrará.
    -->
    <div id="nav" v-if="!ocultarNavegacion">
      <Navbar />
    </div>

    <!-- 
      Contenedor principal donde se carga el contenido del router.
      Dependiendo de si la empresa está seleccionada o no, se aplican diferentes clases CSS para modificar el diseño.
    -->
    <div id="contenedor" :class="{
      'm-select-company': empresaSeleccionada && !ocultarNavegacion,
      'm-unselect-company': !empresaSeleccionada && !ocultarNavegacion,
      'modo-embebido': embebido
    }">
      <!-- Lo que SOFU anuncia desde Remote Config: un mantenimiento o una novedad del servicio. Dentro de la
           Capacitación gratuita (modo embebido) ya lo muestra la página principal. -->
      <AvisoServicio v-if="!embebido" />
      <!-- El componente de router-view renderiza el componente correspondiente a la ruta actual -->
      <router-view />
    </div>

    <!-- Componente para gestionar notificaciones push (no en una sesión de soporte: las del usuario
         llegarían al navegador de soporte) -->
    <NotificacionesPush v-if="rutaResuelta && !esModoSinConexion && !embebido && !enCapacitacion && !soporteActivo" />

    <!-- Sesión de soporte: en qué cuenta se está y cómo salir (ver src/utils/sesionSoporte.js) -->
    <BannerSoporte v-if="sesionSoporte" />

    <!-- Componente de pie de página -->
    <Footer v-if="!ocultarNavegacion" />

    <!-- Flecha para subir al principio de la página. Se oculta con el panel
         del asistente abierto (ocupa la esquina) y al pulsarla el gato salta -->
    <FlechaSubir
      v-if="!embebido && !enCapacitacion"
      :oculto="mostrarAsistente && chatAbierto"
      @cambio-visibilidad="flechaSubirVisible = $event"
      @subir="saltosGato++"
    />

    <!-- Asistente IA (Riskmann IA): chat flotante con navegación asistida.
         Sube mientras la flecha está visible, para no encimarse con ella -->
    <AsistenteIA
      v-if="mostrarAsistente"
      :con-boton-arriba="flechaSubirVisible"
      :saltos-gato="saltosGato"
      @cambio-panel="chatAbierto = $event"
    />

    <!-- Componente para gestionar actualizaciones -->
    <Actualizaciones v-if="!esModoSinConexion && !embebido" />

    <!-- Componente para gestionar aceptación de términos y políticas (soporte no los acepta a nombre
         del usuario). Solo con la ruta ya resuelta: si se montaba antes y la ruta resultaba excluida, el
         modal alcanzaba a abrirse y quedaba pegado al body. -->
    <AceptarTerminos
      v-if="rutaResuelta && !esModoSinConexion && !embebido && !soporteActivo && !rutasExcluidasDeNavBar.includes($route.name)"
    />
  </div>
</template>

<script>
import Vue from "vue";
import loading from "vuejs-loading-screen";
import localforage from "localforage";
import Navbar from "@/components/Navbar/Navbar.vue";
import EmpresaSeleccionada from "./components/Navbar/EmpresaSeleccionada.vue";
import Footer from "./components/Footer/Footer.vue";
import FlechaSubir from "@/components/FlechaSubir.vue";
import NotificacionesPush from './components/NotificacionesPush/NotificacionesPush';
import Actualizaciones from './components/Actualizaciones/Actualizaciones.vue';
import AsistenteIA from "@/components/AsistenteIA/AsistenteIA.vue";
import AceptarTerminos from "./components/Usuario/AceptarTerminos.vue";
import BannerSoporte from "@/components/Soporte/BannerSoporte.vue";
import AvisoServicio from "@/components/Generales/AvisoServicio.vue";
import { iniciarAvisoServicio } from "@/services/avisoServicio";
import { esEmbebido } from "@/utils/embebido";
import { esSesionSoporte } from "@/utils/sesionSoporte";
Vue.use(loading, {
  bg: "rgba(0,0,0,0.6);",
  slot: `
    <div class="px-5 py-3 bg-gray-800 rounded">
      <span class="loader"></span>
    </div>
  `,
});
export default {
  name: 'App',
  components: {
    Navbar,
    EmpresaSeleccionada,
    Footer,
    FlechaSubir,
    NotificacionesPush,
    Actualizaciones,
    AsistenteIA,
    AceptarTerminos,
    BannerSoporte,
    AvisoServicio
  },

  data() {
    return {
      // Rutas excluidad de barras de navegacion (NavBar, NavBarEmpresaSeleccionada, EmpresaSeleccionada)
      // /login/:ruta y /registrarse/:ruta (p. ej. desde el Resumen del en vivo) se ven igual que /login y
      // /registrarse: sin barra ni asistente, que además taparía el botón de WhatsApp.
      rutasExcluidasDeNavBar: ['RedirectTo', 'Entrada', 'Login', 'LoginRedirect', 'Registrarse', 'RegistrarseRuta', 'RegistrarseEmpresaRutaVinculacion', 'SoporteEntrar', 'Precios', 'Precio', 'InstalarPWA', 'PrecioThankYouPage', 'AdminMigracion'],
      // Otras variables
      empresaSeleccionada: null,
      esModoSinConexion: false,
      // Pantalla abierta dentro de la Capacitación gratuita (ver src/utils/embebido.js)
      embebido: esEmbebido(),
      // Pestaña de una sesión de soporte (ver src/utils/sesionSoporte.js)
      sesionSoporte: esSesionSoporte(),
      // Coordinación entre el asistente y la flecha de volver arriba
      flechaSubirVisible: false,
      chatAbierto: false,
      saltosGato: 0,
    }
  },

  computed: {
    // La Capacitación gratuita tiene su propio encabezado. Sin la barra de navegación tampoco corre
    // el vigilante de privilegios del Navbar, que recargaría la página y cortaría el video.
    enCapacitacion() {
      return this.$route.name === "CapacitacionGratuita";
    },
    // El router ya decidió la ruta (al arrancar, mientras su guard consulta Firestore, aún no hay ninguna).
    rutaResuelta() {
      return this.$route.matched.length > 0;
    },
    // También en la página que recibe el permiso: ahí la sesión del usuario empieza antes de recargar.
    soporteActivo() {
      return this.sesionSoporte || this.$route.name === "SoporteEntrar";
    },
    // Barra, empresa seleccionada, pie y asistente: no van en las rutas de entrada, en la
    // Capacitación gratuita ni dentro de su iframe (modo embebido).
    ocultarNavegacion() {
      return this.embebido || this.enCapacitacion || this.rutasExcluidasDeNavBar.includes(this.$route.name);
    },
    mostrarAsistente() {
      return !this.ocultarNavegacion && !this.esModoSinConexion;
    },
  },

  watch: {
    $route(to) {
      setTimeout(() => {
        const fondo = require('@/assets/img/404_fondo.webp');
        document.body.style.backgroundImage = to.matched[0].components.default.name === 'NotFound' || this.comprobarAlerta() ? `url(${fondo})` : "";
      }, 100);
    }
  },

  async mounted() {
    if (!this.embebido) iniciarAvisoServicio();
    // Derivación desde el Campus: ?asistente=<pregunta> → la captura App
    // (AsistenteIA no está montado en todas las rutas) y la consume el panel.
    const params = new URLSearchParams(window.location.search);
    const preguntaAsistente = (params.get("asistente") || "").trim();
    if (preguntaAsistente) {
      // Tope del backend por mensaje (AGENT_MAX_INPUT_CHARS)
      this.$store.state.asistenteIA.preguntaPendiente = preguntaAsistente.slice(0, 500);
    }
    if (params.has("asistente")) {
      // Limpiar DESPUÉS de que el router confirme la navegación inicial:
      // limpiarlo antes no sirve — al confirmar, el router re-agrega el
      // parámetro (fullPath capturado al inicio) y un F5 re-enviaría.
      this.$router.onReady(() => {
        const query = { ...this.$route.query };
        if ("asistente" in query) {
          delete query.asistente;
          this.$router.replace({ query }).catch(() => { });
        }
      });
    }

    //await this.$store.state.obtenerDepartamentosCiudades();
    const modoSinConexion = await localforage.getItem(this.$store.state.colecciones.localforage.modoSinConexion);
    this.esModoSinConexion = modoSinConexion ? modoSinConexion : false;
  },

  methods: {
    obtenerEmpresa(data) {
      this.empresaSeleccionada = data;
    },

    comprobarAlerta() {
      const contenedor = document.getElementById("contenedor");
      if (contenedor.querySelector("div")) {
        return contenedor.querySelector("div").className.startsWith('Alertas');
      }
    }
  }
};
</script>

<style src="@/../public/css/components/forms.css" />
<style src="@/../public/css/components/button.css" />
<style src="@/../public/css/components/badge.css" />
<style src="@/../public/css/components/colors.css" />
<style>
@import url('https://fonts.googleapis.com/css?family=Cutive+Mono');

*,
*:before,
*:after {
  box-sizing: border-box;
}

html {
  font-size: 18px;
}

body {
  font-family: Montserrat, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen-Sans, Ubuntu, Cantarell, "Helvetica Neue", Helvetica, Arial, sans-serif;
  line-height: 1.5;
  background: #ffffff;
  overflow-x: hidden;
}

#app {
  font-family: Avenir, Helvetica, Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}

#nav a {
  color: #2c3e50;
  font-size: 12pt;
}

#nav a.router-link-exact-active {
  color: #073d7a;
}

.btn-xsm {
  --bs-btn-padding-y: .25rem;
  --bs-btn-padding-x: .5rem;
  --bs-btn-font-size: .75rem;
}

.m-select-company {
  padding: 0px;
  margin-top: 95px;
}

.m-unselect-company {
  padding: 0px;
  margin-top: 70px;
}

/* Pantalla dentro de la Capacitación gratuita: sin el margen de la barra fija, y con espacio abajo
   para que el botón de WhatsApp de la página no tape el último botón de la pantalla */
#contenedor.modo-embebido {
  padding: 0px 0px 72px 0px;
  margin-top: 0px;
}

/* En la pantalla embebida sobra la ruta de navegación (Seguridad Vial / …): ocupa alto y saca a la
   persona del paso. */
#contenedor.modo-embebido #breadcrumb {
  display: none;
}

/* En celular, la capacitación muestra el video en un mini reproductor sobre la esquina inferior
   izquierda de la pantalla embebida (PanelPaso.vue marca la raíz): más espacio abajo para poder subir
   el último botón por encima */
.con-mini-reproductor #contenedor.modo-embebido {
  padding-bottom: 190px;
}

.drop__shadow {
  filter: drop-shadow(0 2px 5px rgba(0, 0, 0, 0.7));
}

.centrar__div {
  display: flex;
  justify-content: center;
  align-items: center;
}

.color__Loading {
  color: #073d7a;
}

/*COLORES*/
.primario {
  color: #073d7a;
  text-decoration-color: #073d7a;
}

.secundario {
  color: #F3F3F3;
}

.terciario {
  color: #E6423C;
}

/*modo claro*/
.fondo__light {
  background-color: #F8F9FA;
}

.fondo__light2 {
  background-color: #F3F3F3;
}

/* TEMA CLARO */
body.dark {
  background-color: #2f2f2f;
}

body.dark .text-primary {
  filter: brightness(0%) invert(1);
}


.loader {
  width: 8px;
  height: 40px;
  border-radius: 4px;
  display: block;
  margin: 20px auto;
  position: relative;
  background: currentColor;
  color: #FFF;
  box-sizing: border-box;
  animation: animloader 0.3s 0.3s linear infinite alternate;
}

.loader::after,
.loader::before {
  content: '';
  width: 8px;
  height: 40px;
  border-radius: 4px;
  background: currentColor;
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  left: 20px;
  box-sizing: border-box;
  animation: animloader 0.3s 0.45s linear infinite alternate;
}

.loader::before {
  left: -20px;
  animation-delay: 0s;
}

@keyframes animloader {
  0% {
    height: 48px
  }

  100% {
    height: 4px
  }
}
</style>